// MobileLogSheetKeypad — display row + Direction toggle + digit keypad +
// note input. Extracted from `MobileLogSheet.tsx` to keep the main file
// under the 12KB sheet ceiling (AGENTS.md §3 #10).
//
// Display invariant per v2 §4.5.4 W5.7-fix2: amount is always rendered as
// `Math.abs()` — the +/- UI prefix is a visual signal, never encoded into
// the storage value (the backend stores positive amount_delta).
//
// W35.5: the pure derivations (`deriveDirection` / `isLoggable` /
// `deriveTarget` / `formatCurrency` / `formatDisplay`) + their types moved
// to `./mobileLogSheet.helpers` so this file exports components only.

import { Delete, PencilLine } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { formatCurrency, type DisplayTarget } from "./mobileLogSheet.helpers";

export function DisplayRow({
  amountText,
  target,
  sign,
}: {
  amountText: string;
  target: DisplayTarget;
  sign: "plus" | "minus";
}) {
  const numeric = Number(amountText);
  const safe = Number.isFinite(numeric) && numeric > 0 ? Math.abs(numeric) : 0;
  return (
    <div
      data-component="MobileLogSheetDisplay"
      data-testid="mobile-log-display"
      className="px-s-5 pt-s-1"
    >
      <p className="text-meta text-ink-3 mb-1">記錄金額</p>
      <p className="font-display tabular-nums text-ink-1 text-[44px] leading-none truncate">
        {sign === "minus" && safe > 0 ? "−" : ""}
        {target.unit === "currency"
          ? formatCurrency(safe, target.currency)
          : `${safe}%`}
      </p>
      <p className="text-meta text-ink-3 mt-s-1">
        {target.unit === "currency" ? "輸入金額" : "輸入百分比"}（上限{" "}
        {target.unit === "currency"
          ? formatCurrency(target.max, target.currency)
          : `${target.max}%`}
        ）
      </p>
    </div>
  );
}

export function DirectionToggle({
  value,
  onChange,
}: {
  value: "plus" | "minus";
  onChange: (next: "plus" | "minus") => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="方向"
      data-testid="mobile-log-direction"
      className="mx-s-5 flex items-center gap-1 p-1 rounded-full bg-paper-deep border border-hairline"
    >
      {(
        [
          { v: "plus", label: "+ 支出" },
          { v: "minus", label: "− 收入" },
        ] as const
      ).map((opt) => {
        const active = value === opt.v;
        return (
          <button
            key={opt.v}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.v)}
            data-testid={`mobile-log-direction-${opt.v}`}
            className={cn(
              "press flex-1 min-h-11 rounded-full text-body-sm font-semibold transition-colors",
              active ? "bg-ink-1 text-paper" : "text-ink-2 hover:text-ink-1",
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function DigitKeypad({
  value,
  onChange,
  max,
  unit,
}: {
  value: string;
  onChange: (next: string) => void;
  max: number;
  unit: "currency" | "percent";
}) {
  const press = (ch: string) => {
    if (ch === "⌫") {
      onChange(value.slice(0, -1));
      return;
    }
    if (ch === ".") {
      if (value.includes(".")) return;
      if (value === "") {
        onChange("0.");
        return;
      }
      onChange(value + ".");
      return;
    }
    const candidate = value === "0" ? ch : value + ch;
    const numeric = Number(candidate);
    if (!Number.isFinite(numeric)) return;
    if (unit === "percent" && numeric > max) return;
    onChange(candidate);
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "⌫"];
  return (
    <div
      role="group"
      aria-label="數字鍵盤"
      data-testid="mobile-log-keypad"
      className="mx-s-5 grid grid-cols-3 gap-2"
    >
      {keys.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => press(k)}
          data-testid={`mobile-log-key-${k === "⌫" ? "back" : k}`}
          className="press min-h-12 rounded-2xl bg-paper-elevated border border-hairline text-ink-1 text-title font-semibold tabular-nums flex items-center justify-center"
        >
          {k === "⌫" ? <Delete className="h-5 w-5" aria-hidden /> : k}
        </button>
      ))}
    </div>
  );
}

export function NoteField({
  note,
  onChange,
}: {
  note: string;
  onChange: (next: string) => void;
}) {
  return (
    <label
      data-component="MobileLogSheetNote"
      className="mx-s-5 flex items-center gap-2 px-s-3 py-s-2 rounded-2xl border border-hairline bg-paper-elevated focus-within:outline focus-within:outline-2 focus-within:outline-you-strong focus-within:outline-offset-2"
    >
      <PencilLine className="h-4 w-4 text-ink-3 shrink-0" aria-hidden />
      <input
        type="text"
        placeholder="備註（選填）"
        value={note}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        data-testid="mobile-log-note"
        className="flex-1 bg-transparent focus:outline-none text-body-sm text-ink-1 placeholder:text-ink-3"
      />
    </label>
  );
}